import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Metric } from "../../interfaces/MockData"; // Adjust path as needed

// --- Define the State Shape for this Slice ---
interface MetricsState {
  // Use an object keyed by metric ID for efficient lookups (normalization)
  entities: { [metricId: string]: Metric };
  // Keep track of all metric IDs for easier iteration if needed
  ids: string[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  // Timestamp of the last successful fetch (optional, for caching/refetching logic)
  lastFetched: number | null;
}

// --- Initial State ---
const initialState: MetricsState = {
  entities: {},
  ids: [],
  loading: "idle",
  error: null,
  lastFetched: null,
};

// --- Create the Slice ---
const metricsSlice = createSlice({
  name: "metrics",
  initialState,
  reducers: {
    // *** NEW: Reducer to add or update metrics ***
    // Takes an array of Metric objects as payload
    upsertMetrics: (state, action: PayloadAction<Metric[]>) => {
      action.payload.forEach((metric) => {
        // Check if the metric is valid and has an ID
        if (metric && metric.id) {
          // If the metric ID doesn't exist yet, add it to the ids array
          if (!state.entities[metric.id]) {
            state.ids.push(metric.id);
          }
          // Add or update the metric in the entities object
          state.entities[metric.id] = metric;
        } else {
          console.warn("Attempted to upsert invalid metric:", metric);
        }
      });
      // Optional: If using sorted IDs, you might need to re-sort state.ids here
    },
    // Optional: Add specific reducers here if needed, e.g., for removing single metrics
    // metricRemoved: (state, action: PayloadAction<string>) => { ... },
  },
});

// --- Export Actions ---
// Export the new upsertMetrics action
export const { upsertMetrics } = metricsSlice.actions;

// --- Export Selectors ---
// Select the loading status
export const selectMetricsLoadingStatus = (state: {
  metricsReducer: MetricsState;
}): "idle" | "pending" | "succeeded" | "failed" => state.metricsReducer.loading; // Adjust 'metricsReducer' if name is different in rootReducer

// Select any fetching error
export const selectMetricsError = (state: {
  metricsReducer: MetricsState;
}): string | null => state.metricsReducer.error;

// Select all metric entities (object keyed by ID)
export const selectAllMetricEntities = (state: {
  metricsReducer: MetricsState;
}): { [metricId: string]: Metric } => state.metricsReducer.entities;

// Select all metric IDs (array of strings)
export const selectAllMetricIds = (state: {
  metricsReducer: MetricsState;
}): string[] => state.metricsReducer.ids;

// Select all metrics as an array
export const selectAllMetrics = (state: {
  metricsReducer: MetricsState;
}): Metric[] => {
  // Ensure state.metricsReducer.ids is an array before mapping
  const ids = state.metricsReducer.ids || [];
  return ids.map((id) => state.metricsReducer.entities[id]).filter(Boolean); // Filter out potential undefined if IDs/entities mismatch
};

// Select a single metric by its ID
export const selectMetricById = (
  state: { metricsReducer: MetricsState },
  metricId: string,
): Metric | undefined => state.metricsReducer.entities[metricId];

// --- Export the Reducer ---
export const metricsReducer = metricsSlice.reducer;

/*
// --- Optional: Using createEntityAdapter for Normalization ---
// This is often preferred for managing normalized state.

const metricsAdapter = createEntityAdapter<Metric>({
  // Assumes `metric.id` is the unique identifier
  selectId: (metric) => metric.id,
  // Optional: Keep the IDs sorted, e.g., by name
  // sortComparer: (a, b) => a.name.localeCompare(b.name),
});

interface MetricsStateWithAdapter extends EntityState<Metric> {
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  lastFetched: number | null;
}

const initialStateWithAdapter: MetricsStateWithAdapter = metricsAdapter.getInitialState({
  loading: 'idle',
  error: null,
  lastFetched: null,
});

const metricsSliceWithAdapter = createSlice({
    name: 'metrics',
    initialState: initialStateWithAdapter,
    reducers: {
        // Use adapter's built-in upsert reducer
        upsertMetrics: metricsAdapter.upsertMany, // Takes an array of metrics
        // Or for single metric:
        // upsertMetric: metricsAdapter.upsertOne, // Takes a single metric object
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchMetrics.pending, (state) => {
            state.loading = 'pending';
            state.error = null;
        })
        .addCase(fetchMetrics.fulfilled, (state, action: PayloadAction<Metric[]>) => {
            state.loading = 'succeeded';
            state.error = null;
            state.lastFetched = Date.now();
            metricsAdapter.setAll(state, action.payload); // Use adapter to set entities and ids on full fetch
        })
        .addCase(fetchMetrics.rejected, (state, action) => {
            state.loading = 'failed';
            state.error = action.payload as string || action.error.message || 'Unknown error';
        });
    }
});

// Export adapter actions if defined in reducers
// export const { upsertMetrics } = metricsSliceWithAdapter.actions;


// Export adapter selectors
export const {
  selectAll: selectAllMetricsAdapter,
  selectById: selectMetricByIdAdapter,
  selectIds: selectAllMetricIdsAdapter,
} = metricsAdapter.getSelectors((state: RootState) => state.metricsReducer); // Adjust 'metricsReducer'

// Export the reducer (use only one version, either manual or adapter)
// export default metricsSliceWithAdapter.reducer;

*/
